import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "../../select";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useAgencyAccounts } from "@/hooks/useAgencyAccounts";
import { LeftNavigationData, SettingsNavigationData, type NavigationItem } from "@/data/LeftNavigation";
import { usePermissions } from "@/hooks/usePermissions";
import clsx from "clsx";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import * as Icons from "../Icons";
import { APP_VERSION } from "@/config/version";

interface LeftNavigationProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const navIdle =
  "border border-transparent hover:bg-hover text-muted-foreground hover:text-foreground";
const navActive =
  "border border-transparent bg-[var(--sidebar-active-bg)] text-[var(--sidebar-active-fg)] dark:border-blue-500/30";

const LeftNavigation: React.FC<LeftNavigationProps> = ({ isCollapsed, onToggle }) => {
  const { t } = useTranslation("common");
  const location = useLocation();
  const { logout } = useAuth();
  const { accounts, currentAccount, isAgencyUser, hasMultipleAccounts, switchAccount, loading: accountsLoading } = useAgencyAccounts();
  const { hasPermission } = usePermissions();
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});

  const navLabel = (item: Pick<NavigationItem, "title" | "labelKey">) =>
    item.labelKey ? t(item.labelKey) : item.title;

  const handleLogout = async () => {
    await logout();
  };

  const handleAccountSwitch = async (accountId: string) => {
    const success = await switchAccount(accountId);
    if (!success) {
      console.error("❌ [LeftNavigation] Falha ao trocar conta");
    }
  };

  const shouldShowAccountSelect =
    (isAgencyUser && accounts.length > 0) ||
    (hasMultipleAccounts && accounts.length > 1);

  const filteredMainNav = useMemo(() => {
    return LeftNavigationData.filter((item) => {
      if (!item.permission) return true;
      return hasPermission(item.permission);
    });
  }, [hasPermission]);

  const settingsItem = useMemo(() => {
    return SettingsNavigationData.find((item) => item.path === "/settings");
  }, []);

  const logoutItem = useMemo(() => {
    return SettingsNavigationData.find((item) => item.path === "/login");
  }, []);

  const showSettings =
    !settingsItem?.permission || hasPermission(settingsItem.permission);

  return (
    <div className="w-full overflow-hidden h-full border-r border-border bg-card relative group">
      <button
        onClick={onToggle}
        className={clsx(
          "absolute top-[26px] right-[6px] z-50 w-7 h-7 bg-card hover:bg-hover border border-border rounded-full flex items-center justify-center transition-all duration-200",
          "opacity-0 group-hover:opacity-100",
          isCollapsed && "opacity-100",
          isCollapsed && "right-[20px]"
        )}
        aria-label={isCollapsed ? t("nav.expandMenu") : t("nav.collapseMenu")}
      >
        {isCollapsed ? (
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        ) : (
          <ChevronLeft className="w-4 h-4 text-muted-foreground" />
        )}
      </button>

      <div
        className={clsx(
          "transition-all duration-300 ease-in-out py-4 h-full flex flex-col",
          isCollapsed ? "px-2" : "px-4"
        )}
      >
        <div className="relative flex min-h-0 flex-1 flex-col">
          <div className="no-scrollbar flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overflow-x-hidden">
            {shouldShowAccountSelect && (
              <div
                className={clsx(
                  "w-full flex flex-col gap-3 transition-all duration-300",
                  isCollapsed ? "hidden" : "block"
                )}
              >
                <Select
                  value={currentAccount?.id}
                  onValueChange={handleAccountSwitch}
                  disabled={accountsLoading}
                >
                  <SelectTrigger
                    className={clsx(
                      "transition-all duration-300",
                      isCollapsed ? "w-[50px]" : "w-full"
                    )}
                  >
                    <SelectValue placeholder={t("nav.selectAccount")}>
                      {accountsLoading
                        ? t("nav.loading")
                        : currentAccount?.nome || t("nav.selectAccount")}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {accounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.nome}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            )}

            <button
              className={clsx(
                "w-[48px] h-[48px] rounded-[12px] flex justify-center border border-border items-center text-foreground",
                isCollapsed ? "block" : "hidden"
              )}
              type="button"
              aria-hidden
            >
              <Icons.unfold className="w-[16px] h-[16px]" />
            </button>

            <div className="w-full flex flex-col gap-1">
              {filteredMainNav.map((item, index) => {
                const isComingSoon = !!item.comingSoon;
                const isActive =
                  !isComingSoon &&
                  (item.path === "/"
                    ? location.pathname === "/"
                    : location.pathname === item.path ||
                      location.pathname.startsWith(item.path + "/"));
                const hasChildren = !isComingSoon && !!item.children?.length;
                const isExpanded = expandedItems[item.path] ?? isActive;

                const toggleExpanded = () => {
                  setExpandedItems((current) => ({
                    ...current,
                    [item.path]: !isExpanded,
                  }));
                };

                const itemClassName = clsx(
                  "flex w-full gap-3 px-3 min-h-[48px] rounded-[12px] transition-colors duration-200 relative overflow-hidden",
                  isComingSoon
                    ? "border border-transparent text-muted-foreground/70 cursor-default"
                    : isActive
                      ? navActive
                      : navIdle,
                  isCollapsed
                    ? "justify-center items-center !w-[48px]"
                    : "items-center justify-start"
                );

                const itemContent = (
                  <>
                    <div className="flex-shrink-0">{item.icon}</div>
                    <p
                      className={clsx(
                        "text-[14px] font-medium transition-all duration-300 whitespace-nowrap",
                        isCollapsed ? "opacity-0 hidden scale-95 w-0" : "block"
                      )}
                    >
                      {navLabel(item)}
                    </p>
                    {isComingSoon && !isCollapsed && (
                      <span className="ml-auto shrink-0 rounded-md bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-600 dark:bg-red-500/20 dark:text-red-400">
                        {t("nav.comingSoon")}
                      </span>
                    )}
                    {hasChildren && !isCollapsed && (
                      <ChevronDown
                        className={clsx(
                          "ml-auto h-4 w-4 shrink-0 transition-transform",
                          isExpanded && "rotate-180"
                        )}
                      />
                    )}
                  </>
                );

                return (
                  <div key={index} className="relative w-full">
                    <div className="relative">
                      {isComingSoon ? (
                        <div
                          className={itemClassName}
                          aria-disabled="true"
                          title={t("nav.comingSoon")}
                        >
                          {itemContent}
                        </div>
                      ) : (
                        <Link
                          to={item.path}
                          onClick={(e) => {
                            if (!hasChildren || isCollapsed) return;
                            if (isExpanded) {
                              e.preventDefault();
                              toggleExpanded();
                              return;
                            }
                            toggleExpanded();
                          }}
                          className={itemClassName}
                          aria-expanded={hasChildren ? isExpanded : undefined}
                        >
                          {itemContent}
                        </Link>
                      )}
                    </div>
                    {hasChildren && isExpanded && !isCollapsed && (
                      <div className="ml-6 mt-1 flex flex-col gap-1 border-l border-border pl-2">
                        {item.children!.map((child) => {
                          const childActive = location.pathname === child.path;
                          return (
                            <Link
                              key={child.path}
                              to={child.path}
                              className={clsx(
                                "rounded-lg px-3 py-2 text-xs transition-colors",
                                childActive
                                  ? "bg-[var(--sidebar-active-bg)] text-[var(--sidebar-active-fg)]"
                                  : navIdle
                              )}
                            >
                              {navLabel(child)}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {showSettings && settingsItem && (
                <Link
                  to={settingsItem.path}
                  className={clsx(
                    "flex w-full gap-3 px-3 min-h-[48px] rounded-[12px] transition-colors duration-200",
                    location.pathname === settingsItem.path ||
                      location.pathname.startsWith(settingsItem.path + "/")
                      ? navActive
                      : navIdle,
                    isCollapsed
                      ? "justify-center items-center !w-[48px]"
                      : "items-center justify-start"
                  )}
                >
                  <div className="flex-shrink-0">{settingsItem.icon}</div>
                  <p
                    className={clsx(
                      "text-[14px] font-medium whitespace-nowrap",
                      isCollapsed ? "opacity-0 hidden w-0" : "block"
                    )}
                  >
                    {navLabel(settingsItem)}
                  </p>
                </Link>
              )}

              {logoutItem && (
                <button
                  onClick={handleLogout}
                  className={clsx(
                    "flex w-full gap-3 px-3 min-h-[48px] rounded-[12px] transition-colors duration-200",
                    navIdle,
                    isCollapsed
                      ? "justify-center items-center !w-[48px]"
                      : "items-center justify-start"
                  )}
                >
                  <div className="flex-shrink-0">{logoutItem.icon}</div>
                  <p
                    className={clsx(
                      "text-[14px] font-medium whitespace-nowrap",
                      isCollapsed ? "opacity-0 hidden w-0" : "block"
                    )}
                  >
                    {navLabel(logoutItem)}
                  </p>
                </button>
              )}
            </div>
          </div>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-card to-transparent"
          />
        </div>
        <div
          className={clsx(
            "shrink-0 text-muted-foreground text-xs py-2 transition-all duration-300",
            isCollapsed ? "text-center" : "text-left"
          )}
        >
          {isCollapsed ? `v${APP_VERSION.split(".")[0]}` : `v${APP_VERSION}`}
        </div>
      </div>
    </div>
  );
};

export default LeftNavigation;
